import { useCallback, useEffect, useState } from 'react';
import styles from './index.module.scss';
import Typography from '@primitives/Typography/Typography';
import Button from '@primitives/Button/Button';
import HistoryIcon from '@cp/src/assets/icons/HistoryIcon';
import { CloseIcon } from '@navi/web-ui/lib/icons';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import BackIcon from '@cp/src/assets/images/icons/BackIcon';
import { useNavigate } from 'react-router';
import { toast } from '@primitives/Toast';
import { fetchUploadHistoryPolling } from '../UploadHistory/uploadHistoryDrawerActions';
import { Message } from '../types';
import cx from 'classnames';
import { GenericObject } from '@cp/src/types/CommonConstans';
import UploadHistoryComponent from '../../AllAgents/UploadHistory/UploadHistoryDrawer';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import useColumnDefs from '../UploadHistory/ColumnDefs';
import {
  AgencyAllocationType,
  AgentAllocationType,
  AllocationType,
  convertTypeOfAllocation
} from '../../AllAgents/UploadHistory/types.ts/types';
import CaseAllocationFileUploadContainer from './components/CaseAllocationFileUploadContainer';
import AgencyAllocationFileUploadComponent from './components/AgencyAllocationFileUploadComponent';

type BulkUploadAndUploadHistoryProps = {
  typeOfAllocation: string;
  isAllocatingForAgency?: boolean;
  caseType?: string;
};
type JobData = {
  failureReportFile: string;
};

type AgencyAllocationCaseData = {
  referenceId: string;
  owner: string;
  type: string;
  successCount: number;
  failureCount: number;
  startTime: string;
  uploadStatus: string;
  jobData: JobData;
};

const BulkUploadAndUploadHistory = (props: BulkUploadAndUploadHistoryProps) => {
  const { typeOfAllocation, isAllocatingForAgency = false, caseType } = props;
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const [showUploadHistory, setShowUploadHistory] = useState(false);

  const data = useSelector((store: RootState) => store?.allocationUploadHistory?.data);
  const agencyAllocationData = useSelector(
    (store: RootState) => store?.agencyAllocationUploadHistory?.data
  );

  const navigate = useNavigate();
  const { columnDefs: columnDefsUploadHistory } = useColumnDefs();

  const handleUploadHistoryClick = () => {
    setShowUploadHistory(true);
  };

  const transformRowData = () => {
    return data?.map((item: GenericObject) => {
      return {
        uploadStatus: item.uploadStatus,
        type: item.type,
        successCount: item.successCount,
        failureCount: item.failureCount,
        startTime: item.startTime,
        owner: item.owner,
        referenceId: item.referenceId,
        jobData: item.jobData
      };
    });
  };
  const transformAgencyRowData = useCallback(() => {
    return agencyAllocationData?.map((item: AgencyAllocationCaseData) => {
      return {
        uploadStatus: item?.uploadStatus,
        type: item?.type,
        successCount: item?.successCount,
        failureCount: item?.failureCount,
        startTime: item.startTime,
        owner: item?.owner,
        referenceId: item?.referenceId,
        jobData: item?.jobData
      };
    });
  }, [agencyAllocationData]);

  useEffect(() => {
    if (uploadedFile && hasError) {
      toast('Please upload file under 5 MB', { type: 'error' });
    }
  }, [hasError]);

  const setSuccessToast = async () => {
    const toastMessage = () => {
      return (
        <div>
          <Typography variant={'p3'}>{'1 file uploaded. Please'}</Typography>
          <div className={styles.toastStyle}>
            <Button variant="text" className={styles.toastText} onClick={handleUploadHistoryClick}>
              Go to History
            </Button>
            <Typography variant={'p3'}>to check status</Typography>
          </div>
        </div>
      );
    };
    toast(toastMessage, { type: 'info' });
  };

  return (
    <div>
      <div className={styles.topNavContainer}>
        <div className={cx('cursor-pointer')} onClick={() => navigate(-1)}>
          <BackIcon size={'16'} />
        </div>
      </div>
      <div className={styles.container}>
        {message?.error && (
          <div className={styles.failedBanner}>
            <ErrorIconOutline fillColor1="var(--red-base)" />
            <span className={styles.message}>{message?.message}</span>
            <Button
              variant="text"
              onClick={() => setMessage({ error: false, success: false, message: '' })}
            >
              <CloseIcon />
            </Button>
          </div>
        )}

        <div className={styles.wrapperContainer}>
          <div className="mb-4 mt-14 flex justify-between">
            {typeOfAllocation === AllocationType.Allocate ? (
              <div>
                <Typography variant={'h2'}>{caseType} case allocation</Typography>
              </div>
            ) : (
              <div>
                <Typography variant={'h2'}>{caseType} case deallocation</Typography>
              </div>
            )}
            <div>
              <Button
                variant="text"
                size="medium"
                startAdornment={<HistoryIcon />}
                onClick={handleUploadHistoryClick}
              >
                <Typography color="var(--blue-base)" variant={'h4'}>
                  History
                </Typography>
                <UploadHistoryComponent
                  showUploadHistoryComponent={showUploadHistory}
                  setShowUploadHistoryComponent={setShowUploadHistory}
                  data={isAllocatingForAgency ? agencyAllocationData : data}
                  transformRowData={
                    isAllocatingForAgency ? transformAgencyRowData : transformRowData
                  }
                  columnDefs={columnDefsUploadHistory}
                  fetchUploadHistoryPolling={fetchUploadHistoryPolling}
                  headerText={convertTypeOfAllocation[typeOfAllocation]}
                  typeOfUpload={
                    isAllocatingForAgency
                      ? AgencyAllocationType[typeOfAllocation]
                      : AgentAllocationType[typeOfAllocation]
                  }
                  width={'700px'}
                />
              </Button>
            </div>
          </div>
          {isAllocatingForAgency ? (
            <AgencyAllocationFileUploadComponent
              typeOfAllocation={typeOfAllocation}
              setSuccessToast={setSuccessToast}
            />
          ) : (
            <CaseAllocationFileUploadContainer
              typeOfAllocation={typeOfAllocation}
              isAllocatingForAgency={isAllocatingForAgency}
              uploadedFile={uploadedFile}
              hasError={hasError}
              setUploadedFile={setUploadedFile}
              setHasError={setHasError}
              setMessage={setMessage}
              setSuccessToast={setSuccessToast}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default BulkUploadAndUploadHistory;
